import { useForm } from '@tanstack/react-form';
import { useId, useState } from 'react';
import { z } from 'zod';

import type { GetDoctor, updateDoctorSchema } from '@virtual-care/client';
import { phoneValidator } from '@virtual-care/isomorphic';
import { Button, DialogFooter, Input, Label } from '@virtual-care/shadcn';
import {
  BirthDateField,
  FieldMessage,
  FormMessage,
  GenderField,
  TimezoneCombobox,
  client,
  errorToFormIssue,
} from '@virtual-care/ui';
import { CreateDialog } from '../../components/create-dialog.tsx';
import { PhoneField } from '../../components/phone-field.tsx';
import { SpecialitySelect } from '../../components/speciality-select.tsx';

const schema = z.object({
  email: z
    .string()
    .min(1, { message: 'Email is required' })
    .email({ message: 'Invalid email format' }),
  phoneNumber: z.string(),
  specialityId: z.string().min(1, { message: 'Speciality is required' }),
  firstName: z.string().min(1, { message: 'First name is required' }),
  lastName: z.string().min(1, { message: 'Last name is required' }),
  gender: z.enum(['male', 'female'], { message: 'Please select a gender' }),
  birthDate: z.string().min(1, { message: 'Birth date is required' }),
  timezone: z.string(),
});

const phoneNumberValidator = schema.shape.phoneNumber.superRefine(
  phoneValidator({ validateEmpty: false }),
);

export function UpdateDoctorDialog({
  doctor,
  trigger,
  ...props
}: {
  doctor: GetDoctor;
  trigger: React.ReactNode;
  onSave: () => void;
}) {
  const [open, setOpen] = useState(false);

  const form = useForm({
    defaultValues: {
      email: doctor.email,
      phoneNumber: doctor.phoneNumber,
      specialityId: doctor.specialityId,
      firstName: doctor.firstName,
      lastName: doctor.lastName,
      gender: doctor.gender,
      birthDate: doctor.birthDate,
      timezone: doctor.timezone ?? '',
    },
    validators: {
      onSubmit: schema,
      onSubmitAsync: async ({ value }) => {
        try {
          await client.request('PATCH /doctors/{id}', {
            id: doctor.id,
            ...value,
            gender: value.gender as z.infer<
              typeof updateDoctorSchema.shape.gender
            >,
          });
          setOpen(false);
          return void 0;
        } catch (error) {
          return errorToFormIssue(error);
        }
      },
    },
  });

  return (
    <CreateDialog
      form={form}
      open={open}
      onOpenChange={setOpen}
      title="Edit Doctor"
      subtitle="Update doctor information"
      onSave={props.onSave}
      trigger={trigger}
    >
      <form.Field
        name="email"
        children={(field) => (
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder="doctor@hospital.com"
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />

      <form.Field
        name="firstName"
        children={(field) => (
          <div className="space-y-2">
            <Label htmlFor="firstName">First Name</Label>
            <Input
              id="firstName"
              type="text"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder="John"
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />

      <form.Field
        name="lastName"
        children={(field) => (
          <div className="space-y-2">
            <Label htmlFor="lastName">Last Name</Label>
            <Input
              id="lastName"
              type="text"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder="Doe"
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />

      <form.Field
        name="gender"
        children={(field) => (
          <GenderField
            field={field}
            label="Gender"
            placeholder="Select gender"
            maleLabel="Male"
            femaleLabel="Female"
          />
        )}
      />

      <form.Field
        name="birthDate"
        children={(field) => <BirthDateField label="Birth Date" placeholder="Pick a date" field={field} />}
      />

      <form.Field
        name="phoneNumber"
        validators={{
          onBlurAsync: phoneNumberValidator,
        }}
        children={(field) => <PhoneField field={field} />}
      />

      <form.Field
        name="specialityId"
        children={(field) => <SpecialitySelect field={field} />}
      />

      <form.Field
        name="timezone"
        children={(field) => <TimezoneFieldEN field={field} />}
      />
      <form.Subscribe
        selector={(state) => state.errors}
        children={(errors) => <FormMessage errors={errors} />}
      />
      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <form.Subscribe
          selector={(state) => [state.canSubmit, state.isSubmitting]}
          children={([canSubmit, isSubmitting]) => (
            <Button type="submit" disabled={!canSubmit || isSubmitting}>
              {isSubmitting ? 'Updating...' : 'Update'}
            </Button>
          )}
        />
      </DialogFooter>
    </CreateDialog>
  );
}

function TimezoneFieldEN<
  TF extends {
    state: any;
    handleChange: (value: string) => void;
  },
>({ field }: { field: TF }) {
  const id = useId();
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>Timezone</Label>
      <TimezoneCombobox
        id={id}
        value={field.state.value || ''}
        onChange={(tz) => field.handleChange(tz)}
        className="w-full"
      />
      <FieldMessage errors={field.state.meta.errors} />
    </div>
  );
}
